// src/components/admin/AdminLayout.jsx
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

export default function AdminLayout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);

  // Check auth on mount
  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const res = await fetch("/.netlify/functions/me", {
          method: "GET",
          credentials: "include",
        });

        if (res.status === 401 || res.status === 403) {
          if (!cancelled) {
            navigate("/login", { replace: true });
          }
          return;
        }

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.ok) {
          if (!cancelled) {
            setError(data.error || "Failed to verify authentication");
            setLoading(false);
          }
          return;
        }

        if (!cancelled) {
          setUser(data.user || null);
          setLoading(false);
        }
      } catch (err) {
        console.error("Auth check error:", err);
        if (!cancelled) {
          setError("Network error while checking login");
          setLoading(false);
        }
      }
    }

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    async function loadNotificationCount() {
      try {
        const res = await fetch("/.netlify/functions/get-notifications", {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) return;
        if (!cancelled) {
          setNotificationCount(Number(data.newCount) || 0);
        }
      } catch (err) {
        console.error("Notification count error:", err);
      }
    }

    loadNotificationCount();
    const id = setInterval(loadNotificationCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  async function handleLogout() {
    try {
      await fetch("/.netlify/functions/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      navigate("/login", { replace: true });
    }
  }

  if (loading) {
    return (
      <div style={styles.full}>
        <p>Checking admin access...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.full}>
        <div style={styles.errorCard}>
          <h1>Admin</h1>
          <p style={{ color: "#b91c1c" }}>{error}</p>
          <button onClick={() => navigate("/login")}>Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.shell}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <span style={styles.brand}>Admin</span>
          <span style={styles.subtleUser}>
            {user?.email || "admin"}
          </span>
        </div>

        <nav style={styles.nav}>
          <NavLink
            to="/admin"
            end
            style={({ isActive }) =>
              isActive ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem
            }
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/admin/contact"
            style={({ isActive }) =>
              isActive ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem
            }
          >
            Contact
          </NavLink>
          <NavLink
            to="/admin/blog"
            style={({ isActive }) =>
              isActive ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem
            }
          >
            Blog
          </NavLink>
          <NavLink
            to="/admin/notifications"
            style={({ isActive }) =>
              isActive ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem
            }
          >
            <span>Notifications</span>
            <span style={styles.navCount}>({notificationCount})</span>
          </NavLink>
          <NavLink
            to="/admin/events"
            style={({ isActive }) =>
              isActive ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem
            }
          >
            Events
          </NavLink>
          <NavLink
            to="/admin/videos"
            style={({ isActive }) =>
              isActive ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem
            }
          >
            Videos
          </NavLink>
          <NavLink
            to="/admin/stats"
            style={({ isActive }) =>
              isActive ? { ...styles.navItem, ...styles.navItemActive } : styles.navItem
            }
          >
            Stats
          </NavLink>
        </nav>

        <button style={styles.logoutButton} onClick={handleLogout}>
          Log out
        </button>
      </aside>

      {/* Main content area */}
      <main style={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}

const styles = {
  full: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "system-ui, sans-serif",
  },
  shell: {
    display: "grid",
    gridTemplateColumns: "260px minmax(0, 1fr)",
    minHeight: "100vh",
    fontFamily: "system-ui, sans-serif",
    background: "#f3f4f6",
  },
  sidebar: {
    padding: "24px",
    borderRight: "1px solid #e5e7eb",
    background: "#111827",
    color: "white",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  sidebarHeader: {
    marginBottom: 16,
  },
  brand: {
    display: "block",
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  subtleUser: {
    marginTop: 4,
    fontSize: "0.85rem",
    color: "#9ca3af",
  },
  nav: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginTop: 8,
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    borderRadius: "999px",
    color: "#d1d5db",
    textDecoration: "none",
    fontSize: "0.9rem",
  },
  navCount: {
    fontSize: "0.82rem",
    color: "inherit",
    opacity: 0.95,
  },
  navItemActive: {
    background: "#f9fafb",
    color: "#111827",
  },
  logoutButton: {
    marginTop: "auto",
    padding: "8px 12px",
    borderRadius: "999px",
    border: "1px solid #4b5563",
    background: "transparent",
    color: "#e5e7eb",
    cursor: "pointer",
    fontSize: "0.85rem",
    alignSelf: "flex-start",
  },
  main: {
    padding: "24px",
    boxSizing: "border-box",
  },
  errorCard: {
    padding: "16px 20px",
    borderRadius: 12,
    background: "white",
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
  },
};
